/**
 * Markdown-to-Word formatter — public API.
 *
 * Orchestrates the three layers:
 *   1. Config (what styles to apply)
 *   2. Parser (what the text is)
 *   3. Renderer (how to write it)
 *
 * Single entry point: formatAndInsert()
 */

/* global Word */

import { mergeConfig } from "./formatterConfig";
import type { FormatterConfig } from "./formatterConfig";
import { parseMarkdown } from "./markdownParser";
import { insertPendingImages, pendingImages, renderNodes } from "./wordRenderer";

// Re-export types that consumers may need
export type { FormatterConfig } from "./formatterConfig";
export type { ParsedNode, InlineSegment } from "./types";
export { parseMarkdown, needsFormatterRender } from "./markdownParser";
export { DEFAULT_CONFIG } from "./formatterConfig";

/**
 * Parse markdown content and insert formatted paragraphs into Word.
 *
 * This is the single function that replaces the naive line-by-line
 * insertion in wordWriter.ts. It handles:
 * - Section heading deduplication (skip parent, keep children, stop at boundary)
 * - Markdown headings → Word Heading 2/3 styles
 * - Bold, [NOT FOUND], [REVIEWER:], [FILL:] → run-level formatting
 * - Markdown tables → Word tables
 * - Times New Roman 11pt on every run (not just paragraph-level)
 * - Justified alignment, proper spacing
 *
 * @param context - The Word request context (caller's Word.run scope) — used
 *                  to sync between every node insertion so the proxy chain
 *                  doesn't silently drop everything past the first paragraph.
 * @param headingParagraph - The scaffold heading paragraph to insert content after
 * @param markdownContent - The agent's raw markdown output
 * @param sectionNumber - The parent section number (e.g. "9.2") for heading dedup
 * @param configOverrides - Optional partial config to override DEFAULT_CONFIG
 */
export async function formatAndInsert(
  context: Word.RequestContext,
  headingParagraph: Word.Paragraph,
  markdownContent: string,
  sectionNumber: string,
  configOverrides?: Partial<FormatterConfig>,
): Promise<void> {
  const config = mergeConfig(configOverrides);

  // In demo mode, normalize [NOT FOUND - ...] markers to clean [TO FILL].
  // In production mode, keep the detailed markers with highlights.
  let contentToFormat = markdownContent;
  if (config.demoMode) {
    contentToFormat = markdownContent
      .replace(/\[NOT FOUND\s*-\s*(?:NOT IN SOURCE|RETRIEVAL FAILURE|AWAITING DOCUMENT)(?::\s*[^\]]+)?\]/g, "[TO FILL]")
      .replace(/\[NOT FOUND\s*-\s*[^\]]+\]/g, "[TO FILL]");
  }
  const nodes = parseMarkdown(contentToFormat, sectionNumber);

  if (nodes.length === 0) {
    console.warn(`[formatter] No content nodes parsed for §${sectionNumber}`);
    return;
  }

  await renderNodes(context, headingParagraph, nodes, config);

  // Capture pending images BEFORE they're cleared — we'll process them
  // in a separate Word.run after this one completes. This is critical:
  // inserting images inside the caller's Word.run would exceed its
  // timeout on sections with many images (29 in section 3).
  const imagesToInsert = [...pendingImages];
  pendingImages.length = 0; // Clear for next section

  // Schedule image insertion as a SEPARATE async operation.
  // Text content is committed by the caller's Word.run context.sync().
  // Images are inserted afterwards in their own Word.run calls —
  // one per image for isolation (a failed image doesn't block others).
  if (imagesToInsert.length > 0) {
    // We can't use the caller's context (it may be closed after sync).
    // Instead, queue a microtask that runs AFTER the caller's Word.run
    // completes. Each image gets its own Word.run for resilience.
    setTimeout(async () => {
      let inserted = 0;
      for (const { url, alt, inCell } of imagesToInsert) {
        try {
          await _insertSingleImage(url, alt, config, { inCell });
          inserted++;
        } catch (err) {
          console.warn(`[formatter] Image insert failed for ${url}:`, err);
        }
      }
    }, 500); // Small delay to let the text Word.run complete first
  }
}

/**
 * Insert a single image into the Word document.
 *
 * Runs in its OWN Word.run context — isolated from the text insertion.
 * Finds the placeholder paragraph by searching for "[Image: {alt}]" text,
 * then replaces it with the actual image.
 *
 * If the image fetch fails, the placeholder text is updated to show
 * a failure message (graceful degradation).
 */
async function _insertSingleImage(
  url: string,
  alt: string,
  config?: FormatterConfig,
  opts?: { inCell?: boolean },
): Promise<void> {
  const imgConfig = config?.image ?? {
    fetchTimeout: 5000,
    failurePlaceholder: "[Image could not be loaded]",
    captionColor: "#808080",
  };
  const fontName = config?.font?.name ?? "Times New Roman";

  const apiBase = (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE || "/api";

  const fullUrl = url.startsWith("/api/")
    ? url.replace("/api/", `${apiBase}/`)
    : url.startsWith("http")
      ? url
      : `${apiBase}/${url}`;

  // Fetch image with timeout.
  //
  // credentials: "include" — required symmetry with the first-pass
  // insertPendingImages fetch. Without it, Word's cross-origin
  // iframe context omits the JWT cookie + some browsers refuse the
  // cookieless cross-origin request outright (images silently fail;
  // placeholder stays in doc). Images are also in the server's JWT
  // allow-list (server/main.py::jwt_guard_middleware), so this is
  // belt-and-suspenders.
  let base64: string;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), imgConfig.fetchTimeout);
    const resp = await fetch(fullUrl, {
      signal: controller.signal,
      credentials: "include",
    });
    clearTimeout(timer);

    if (!resp.ok) {
      console.warn(`[formatter] Image fetch failed: ${fullUrl} (${resp.status})`);
      return;
    }

    const blob = await resp.blob();
    base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1] || "");
      };
      reader.readAsDataURL(blob);
    });

    if (!base64) return;
  } catch (err) {
    console.warn(`[formatter] Image fetch error for ${url}:`, err);
    return;
  }

  // Insert into Word in its own context
  await Word.run(async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    // Find the placeholder paragraph by matching the [Image: prefix.
    // Use includes() for resilience — Word may modify whitespace,
    // encode special characters (em dashes, etc.), or add trailing
    // characters after context.sync(). Match on the first 30 chars
    // of the alt text to avoid false positives while being tolerant.
    const matchKey = alt.slice(0, 30);
    for (let i = 0; i < paragraphs.items.length; i++) {
      const paraText = paragraphs.items[i].text.trim();
      if (paraText.startsWith("[Image:") && paraText.includes(matchKey)) {
        const para = paragraphs.items[i];
        para.clear();
        para.spaceAfter = 4;
        para.spaceBefore = 4;
        para.alignment = "Centered";
        const picture = para.insertInlinePictureFromBase64(base64, "Start");
        // Constrain the width. Body figures cap at 5 inches (360 pt); a figure
        // embedded in a table cell (e.g. the CMC Photograph Comparison table)
        // caps at 2 inches (144 pt) so a single thumbnail doesn't blow the
        // column out to full width. lockAspectRatio keeps tall/narrow images
        // from consuming a full page / row.
        picture.width = opts?.inCell ? 144 : 360;
        picture.lockAspectRatio = true;

        // Caption below — tight spacing to keep it visually attached. Skipped
        // for in-cell figures: the comparison row already labels the cell, and
        // a caption paragraph would just add height inside the cell.
        if (alt && !opts?.inCell) {
          const caption = para.insertParagraph(alt, "After");
          caption.font.name = fontName;
          caption.font.size = 9;
          caption.font.italic = true;
          caption.font.color = imgConfig.captionColor;
          caption.alignment = "Centered";
          caption.spaceAfter = 6;
          caption.spaceBefore = 2;
        }

        await context.sync();
        return;
      }
    }
  });
}
